/**
 * tutorStream — SSE client for Tutor::ChatRunner's turn stream
 * (`POST /web/courses/:manual_version_id/tutor/conversations/:id/stream`,
 * TASK-010 Phase 2 API Contract). Uses `@microsoft/fetch-event-source`
 * because the endpoint is a POST (native EventSource only does GET) and
 * needs the CSRF header the rest of this app's session-auth JSON calls use.
 *
 * The pure, framework-free pieces (event types, `parseTutorEvent`,
 * `TutorTurnState`/`EMPTY_TURN_STATE`, `applyTutorEvent`) moved to
 * `@arianna/reader-core/tutor` in TASK-019 Phase 4 ("Package condivisi —
 * estrazione tutor") so mobile can reuse them unchanged — re-exported below
 * so no other file in this app has to change its import. Only
 * `streamTutorMessage` (the network/DOM transport, built on
 * `@microsoft/fetch-event-source`) stays here.
 */

import { fetchEventSource, EventStreamContentType } from '@microsoft/fetch-event-source'
import { getCsrfToken } from './syncApi.js'
import { parseTutorEvent, type TutorStreamEvent } from '@arianna/reader-core/tutor'

export type {
  TutorTextEvent,
  TutorCitationEvent,
  TutorLaunchQuizAction,
  TutorOpenSectionAction,
  TutorActionEvent,
  ToolStepName,
  TutorToolStepEvent,
  TutorDoneEvent,
  TutorLimitReachedEvent,
  TutorErrorEvent,
  TutorStreamEvent,
  TutorTurnState,
} from '@arianna/reader-core/tutor'
export {
  TOOL_STEP_NAMES,
  parseTutorEvent,
  EMPTY_TURN_STATE,
  applyTutorEvent,
} from '@arianna/reader-core/tutor'

// ---------------------------------------------------------------------------
// streamTutorMessage — the network/transport half.
// ---------------------------------------------------------------------------

export interface StreamTutorMessageArgs {
  manualVersionId: number
  conversationId: string
  message: string
  onEvent: (event: TutorStreamEvent) => void
  signal?: AbortSignal
}

export async function streamTutorMessage({
  manualVersionId,
  conversationId,
  message,
  onEvent,
  signal,
}: StreamTutorMessageArgs): Promise<void> {
  await fetchEventSource(
    `/web/courses/${manualVersionId}/tutor/conversations/${conversationId}/stream`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-CSRF-Token': getCsrfToken(),
      },
      credentials: 'same-origin',
      body: JSON.stringify({ message }),
      // Keep streaming while the tab is backgrounded — without this the
      // library closes the connection on visibilitychange and (combined
      // with the always-throw onerror below) would abort an in-flight turn
      // just from switching tabs.
      openWhenHidden: true,
      ...(signal != null ? { signal } : {}),
      async onopen(response) {
        const contentType = response.headers.get('content-type') ?? ''
        if (response.ok && contentType.includes(EventStreamContentType)) return
        throw new Error(`tutor stream failed: HTTP ${response.status}`)
      },
      onmessage(ev) {
        const parsed = parseTutorEvent(ev.data)
        if (parsed != null) onEvent(parsed)
      },
      onerror(err) {
        // ALWAYS rethrow: fetch-event-source's default behaviour on error is
        // to retry with backoff, which for a POST turn would resend
        // `message` and create a duplicate user turn server-side (the
        // server has no idempotency key for this endpoint). One-shot only —
        // the caller is responsible for surfacing the failure.
        throw err instanceof Error ? err : new Error(String(err))
      },
    },
  )
}
